import * as R from 'ramda';
import { ApiReducer, makeApiAction, makeApiReducer, makeRsaaTypes } from '../../../../core/api/rsaa';
import { addError, addLoading, noLoadingNoError, getLoading } from '../../../../core/api/module';
import { Endpoints } from '../../../../core/api/endpoints';
import { AppState } from '../../../../core/redux/appState';
import { ActionType } from '../../../../core/redux/actionType';
import { DevPlanCollaborator } from '../models';


const KEY_ACTION = ActionType.GetDevPlanCollaboratorsRequest;
const ACTIONS    = [
  ActionType.GetDevPlanCollaboratorsRequest,
  ActionType.GetDevPlanCollaboratorsResponse,
  ActionType.GetDevPlanCollaboratorsError,
];


type Payload = DevPlanCollaborator[];


type Meta = {
  projectId: number;
};


/**
 * Dispatch this to retrieve all dev plan collaborators for the current project/participant pair
 *
 * @param projectId - the database Id for the project the dev plan for which collaborators are to be retrieved
 * @param participantId - the database Id for the participant for whom we're retrieving collaborators
 */
export const getDevPlanCollaboratorsAction = (
  projectId: number,
  participantId: number,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.DevPlanCollaborators,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify(
      {
        projectId:     projectId,
        participantId: participantId,
      },
    ),
    types:    makeRsaaTypes<Payload, Meta>(
      ACTIONS,
      {
        projectId: projectId,
      },
    ),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${projectId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const { projectId } = action.meta;

  const metaTag = `${projectId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  if (!payload) {
    return successState;
  }

  return R.assocPath(
    ['devPlan', 'collaborators', `${projectId}`],
    payload,
    successState,
  );
};

export const getDevPlanCollaboratorsReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
